import { useState, useContext } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { AppContext } from '../../../App';
import { ITrainingSet } from '../../../types/my-types';
import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import { useMutation } from '@tanstack/react-query';
import useGlynt from '../../hooks/useGlynt';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTrainingSetUpdate } from '../../hooks/useTrainingSetUpdate';
interface ITrainingSetAddDocumentsByIdProps {
  trainingSet?: ITrainingSet;
}

const TrainingSetAddDocumentsById: React.FC<ITrainingSetAddDocumentsByIdProps> = ({
  trainingSet,
}) => {
  const { setToast } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [documentIds, setDocumentIds] = useState('');
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const glynt = useGlynt();

  const { mutateAsync: updateTrainingSet, isPending: isLoading } = useTrainingSetUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['trainingSet']
      });
      queryClient.invalidateQueries({
        queryKey: ['documents']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Documents added successfully.',
      });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      let message = error.message || 'Sorry, failed to upload documents.';
      setToast({
        variant: 'error',
        open: true,
        message,
      });
      setError(message);
    },
  });

  const isValid = (ids: string): boolean => {
    // Trim spaces and split by commas
    const items = ids.split(',').map((item) => item.trim());

    const regex = /^[a-zA-Z0-9]{6}$/;

    return items.every((item) => regex.test(item));
  };

  const handleSubmit = () => {
    if (isValid(documentIds)) {
      const sanitizedDocumentIds = documentIds.split(',').map((id) => id.trim());

      setError(null);

      updateTrainingSet({
        trainingSetId: trainingSet!.id,
        data: {
          documents: trainingSet!.documents.concat(sanitizedDocumentIds),
        },
      });
    } else {
      setError('Invalid input.');
    }
  };

  const openModal = () => {
    setOpen(true);
    setError(null);
    setDocumentIds('');
  };

  return (
    <div id="add-docs-by-id" style={{ display: 'inline' }}>
      <IconButtonWhiteBg
        disabled={false}
        onClick={() => openModal()}
        id="add-docs-by-id-button"
        cypressId="btn-training-set-add-documents-by-id"
        tooltip="Add Documents By ID"
      >
        <AddToPhotosIcon style={{ fontSize: '14px', color: 'black' }} />
      </IconButtonWhiteBg>

      <Dialog
        id="add-docs-dialog"
        style={{ display: `${open ? '' : 'none'}` }}
        maxWidth="md"
        open={open}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') {
            setOpen(false);
          }
        }}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Add Documents By Document ID</DialogTitle>
        <DialogContent>
          <Grid container>
            <TextField
              id="document-id-input"
              label="Document IDs, comma separated"
              variant="outlined"
              fullWidth
              margin="normal"
              value={documentIds}
              data-cy="input-document-id"
              onChange={(e) => {
                const input = e.target.value;
                setDocumentIds(input);
                setError(isValid(input) ? null : 'Invalid input.');
              }}
            />
          </Grid>
          {error ? (
            <Typography style={{ color: 'red', fontSize: '12px' }}>{error}</Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            id="add-docs-by-id-dialog-cancel"
            onClick={() => {
              setOpen(false);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            id="add-docs-by-id-dialog-submit"
            data-cy="btn-add-docs-by-id-submit"
            onClick={handleSubmit}
            color="primary"
            disabled={!!error || isLoading || !documentIds}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TrainingSetAddDocumentsById;
